import React, { CSSProperties, FC, Ref } from 'react';
import clsx from 'clsx';
import { ExpandCircleLeft, ExpandCircleRight } from 'components/icons';
import { cleanString } from 'helpers/utils/util';

export type SliderNavigationProps = {
  arrows?: boolean;
  prevArrowStyles?: CSSProperties;
  nextArrowStyles?: CSSProperties;
  navigationPrevRef?: Ref<HTMLButtonElement>;
  navigationNextRef?: Ref<HTMLButtonElement>;
  ariaLabelOfPreviousButton?: string;
  ariaLabelOfNextButton?: string;
  uniqueFFIDText?: string;
  position?: 'left' | 'right';
  cardArrows?: boolean;
  arrowsClassName?: string;
  slideDescription?: string;
};

const SliderNavigation: FC<SliderNavigationProps> = ({
  arrows,
  prevArrowStyles,
  nextArrowStyles,
  navigationPrevRef,
  navigationNextRef,
  ariaLabelOfPreviousButton,
  ariaLabelOfNextButton,
  uniqueFFIDText,
  position = 'right',
  cardArrows,
  arrowsClassName,
  slideDescription,
}) => {
  const uniqueFFIDTextValue =
    uniqueFFIDText === 'hero-banner' ? 'hero-banner-slider-nav' : '';

  const cardArrowCss = cardArrows
    ? 'absolute right-24 -bottom-45'
    : `absolute right-0 -bottom-20 ${uniqueFFIDTextValue}`;

  const arrowValue = arrows ? cardArrowCss : 'hidden';

  const arrowsClasses = clsx([
    arrowValue,
    position === 'left' ? 'left-0' : '',
    arrowsClassName,
  ]);

  return (
    <div
      className={arrowsClasses}
      data-ffid={`ctr-slider-${cleanString(uniqueFFIDText ?? '')}-arrow`}
      data-testid="slider-nav-arrows"
    >
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {slideDescription}
      </div>

      <div className="flex gap-20">
        <button
          ref={navigationPrevRef}
          type="button"
          aria-label={ariaLabelOfPreviousButton}
          data-ffid={`btn-prev-${cleanString(uniqueFFIDText ?? 'arrow')}`}
          className="cursor-pointer border-0 bg-transparent p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span style={prevArrowStyles}>
            <ExpandCircleLeft
              className="w-40 text-primary dark:text-white"
              aria-hidden="true"
              data-ffid={`icon-prev-${cleanString(uniqueFFIDText ?? 'arrow')}`}
            />
          </span>
        </button>

        <button
          ref={navigationNextRef}
          type="button"
          aria-label={ariaLabelOfNextButton}
          data-ffid={`btn-next-${cleanString(uniqueFFIDText ?? 'arrow')}`}
          className="cursor-pointer border-0 bg-transparent p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span style={nextArrowStyles}>
            <ExpandCircleRight
              className="w-40 text-primary dark:text-white"
              aria-hidden="true"
              data-ffid={`icon-next-${cleanString(uniqueFFIDText ?? 'arrow')}`}
            />
          </span>
        </button>
      </div>
    </div>
  );
};

export default SliderNavigation;